import { Alert, Button, Stack, Typography } from '@mui/material';
import { useFieldArray, useForm } from "react-hook-form";
import StepperDUC from "./StepperDUC";
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SubRecipientInfoComponent from "./components/SubRecipientInfoComponent";
import { useCheckSubApplicantsMutation, useGetSubApplicantsQuery } from "../../api/ducApiSlice";
import { useEffect, useRef, useState } from "react";
import SubRecipientsErrorModal from "./components/SubRecipientsErrorModal";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colours } from '../../StyleConstants';
import { Box } from '@mui/system';

interface FormValuesRecipient {
    subApplicants: {
        firstName: string
        lastName: string
        email: string
    }[]
}

const SubRecipients = (props: any) => {

    const [checkSub] = useCheckSubApplicantsMutation();
    const [openModal, setOpenModal] = useState(false);
    const [error, setError] = useState<string[]>([])
    const [firstRender, setFirstRender] = useState(false)
    const [prefilledFromGeneral, setPrefilledFromGeneral] = useState(false)


    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<FormValuesRecipient>({
        defaultValues: {
            subApplicants: props?.DUCInfo?.subApplicants || props?.prevData?.data?.subApplicants || [{
                firstName: "",
                lastName: "",
                email: ""
            }]
        }
    });

    useEffect(() => {
        if(!props.prevData?.data){
            setFirstRender(true)
        }
        else if(props.prevData?.data && firstRender){
            setValue("subApplicants", props.prevData?.data?.subApplicants)

        }
    }, [props.prevData]);


    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: "subApplicants"
    })

    // Auto-populate sub-applicants when a PI starts a brand new AIAN group request: pull in
    // whoever is already approved on their General DUC, so they don't have to retype the same
    // list. Only applies the very first time (fresh request, not editing/adding/renewing an
    // existing AIAN, and nothing already entered in this session) - if it doesn't find anything,
    // the PI still sees the normal blank row and can add sub-applicants manually.
    //
    // NOTE: this reads props.DUCInfo?.ducType rather than props.type/props.ducType. DucFlows.tsx
    // forwards this component's "type" prop from props.formType, which is never actually set
    // anywhere upstream (RenderCurrentDucStep's caller only passes `type`, not `formType`), so
    // props.type here is always undefined - a pre-existing, previously-harmless mismatch since
    // nothing in this component read it before. props.DUCInfo?.ducType is populated reliably by
    // UseGetStepHookDucProcess and is what other steps (e.g. Statement.tsx) already key off of.
    const isFreshAianGroupRequest = props?.DUCInfo?.ducType === "AIAN"
        && !props.isEdit
        && !props.isAddSub
        && !props?.prevData?.data?.subApplicants?.length
        && !props?.DUCInfo?.subApplicants?.length

    const { data: piSubApplicants } = useGetSubApplicantsQuery(
        { username: props.username },
        { skip: !isFreshAianGroupRequest || !props.username }
    )

    const hasPrefilledRef = useRef(false)

    useEffect(() => {
        if (!isFreshAianGroupRequest || hasPrefilledRef.current || !piSubApplicants) {
            return
        }

        const approvedGeneralSubApplicants = (piSubApplicants?.GENERAL || []).filter(
            (sub: any) => sub.ducStatus === "Approved" && !sub.isRemoved && !sub.isCancelled
        )

        if (approvedGeneralSubApplicants.length > 0) {
            replace(approvedGeneralSubApplicants.map((sub: any) => {
                const [firstName, ...rest] = (sub.fullName || "").trim().split(/\s+/)
                return {
                    firstName: firstName || "",
                    lastName: rest.join(" "),
                    email: sub.email || ""
                }
            }))
            setPrefilledFromGeneral(true)
        }

        // Either way (found matches or not), don't try again - avoids clobbering edits the
        // PI makes afterward if this effect re-runs due to a background refetch.
        hasPrefilledRef.current = true
    }, [isFreshAianGroupRequest, piSubApplicants, replace]);

    // Add-sub flow skips the Recipients and Statement steps, so DUCInfo would otherwise reach
    // the final payload without the PI's details or the Research Data Use Statement (LC-5952:
    // the DocuSign document then renders an empty statement). Prefill those fields from the
    // approved DUC exactly once, and NEVER overwrite a value the user has already provided -
    // e.g. a statement they typed after going Back to the Statement step. (This used to happen
    // as a render-time side effect in checkIfNew, which clobbered freshly typed values with the
    // stale record from a previous attempt.)
    const hasPrefilledAddSubRef = useRef(false)

    useEffect(() => {
        const prev = props?.prevData?.data
        if (!props.isAddSub || !prev || hasPrefilledAddSubRef.current) {
            return
        }

        props.setDUCInfo((current: any) => ({
            ...current,
            firstName: current?.firstName ?? prev?.firstName,
            lastName: current?.lastName ?? prev?.lastName,
            email: current?.email ?? prev?.email,
            phone: current?.phone ?? prev?.phone,
            degree: current?.degree ?? prev?.degree,
            institution: current?.institution ?? prev?.institution,
            city: current?.city ?? prev?.city,
            state: current?.state ?? prev?.state,
            country: current?.country ?? prev?.country,
            statement: current?.statement || prev?.statement,
        }))

        hasPrefilledAddSubRef.current = true
    }, [props.isAddSub, props?.prevData]);

    const handleClick = (newData: FormValuesRecipient) => {
        // Require at least one sub-applicant
        if (newData?.subApplicants?.length === 0) {
            setError(["You need to add at least one Sub-Applicant to continue"])
            setOpenModal(true)
            return
        }

        // If adding sub-applicants, ensure at least one is new compared to previous data
        const prevSubs = props?.prevData?.data?.subApplicants
        if (props.isAddSub && prevSubs && (JSON.stringify(newData.subApplicants) === JSON.stringify(prevSubs))) {
            setError(["You need to add at least one new Sub-Applicant to continue"])
            setOpenModal(true)
            return
        }

        const ducDetailsId = props?.data?.ducDetailsId || 0
        const payload = { data: { ...props.DUCInfo, ...newData, ducDetailsId } }

        checkSub(payload).then((response: any) => {
            const hasErrorArray = response?.error && "data" in response.error && Array.isArray(response.error?.data?.errors)
            const errorList = hasErrorArray ? response.error.data.errors as string[] : []

            // Accept multiple "success" shapes:
            // 1) response.data === null (legacy BE)
            // 2) response.data?.valid === true
            // 3) response.data?.errors exists but is an empty array
            const beData = response?.data
            const beValidFlag = beData?.valid === true
            const beNoErrorsArray = Array.isArray(beData?.errors) && beData.errors.length === 0
            const legacyNullSuccess = beData === null

            if (!hasErrorArray && (legacyNullSuccess || beValidFlag || beNoErrorsArray)) {
                props.setDUCInfo({ ...props.DUCInfo, ...newData });
                props.setCurStep(props.curStep + 1)
            } else if (hasErrorArray) {
                setError(errorList)
                setOpenModal(true)
            } else {
                // Fallback: if BE returned something unexpected but not an explicit error,
                // block advance and show a generic message to avoid a stuck UI without feedback.
                setError(["Could not validate sub-applicants. Please review the entries or try again."])
                setOpenModal(true)
            }
        })
    }


    const handleClickBack = () => {
        props.setCurStep(props.curStep - 1)
    }

    const handleClickAdd= () => {
        append({
            firstName: "",
            lastName: "",
            email: ""
        })
    }

    const handleClickRemove = (id: number) => {
        remove(id)
    }



    const checkIfNew = (email: string, index: number) => {
        const subApp = props?.prevData?.data?.subApplicants

        if (props.isAddSub && subApp) {
            if(fields.findIndex((field) => field.email === email) < index){
                return true
            }

            return !(subApp.findIndex((sub: { email: string; }) => sub.email === email) + 1);


        }
        else if (props.isEdit && props.isSecond && subApp){

            // Blocks at indices within the original prevData count are pre-populated (disabled).
            // Any block added beyond that count is new and must always remain editable/removable.
            return index >= subApp.length;
        }
        return true
    }


    return (
        <Stack flexDirection="column" gap={3}>
            <Stack flexDirection="column" gap={4}>
                <StepperDUC
                    activeStep={props.curStep}
                    stepLabels={props.stepLabels} />
            </Stack>
            <Stack alignItems={"flex-start"} gap={4}>
                <Box>
                    <Typography variant={"h2"} fontSize={"20px"} fontWeight={"bold"}>Sub-applicants</Typography>
                    <Typography fontSize={"14px"}>List all sub-applicants that require data use access.
                        Sub-applicants will not need to fill or sign individual request. Each sub-applicant will have to complete training after approval.
                    </Typography>
                </Box>

                <Alert
                    icon={<InfoOutlinedIcon
                        fontSize="inherit"
                        sx={{ color: colours.notifications.info }} />}
                    sx={{ backgroundColor: colours.notifications.info10 }
                    }
                >
                    <Typography fontSize={"14px"}><strong>Sub-applicants must be affiliated with your own institution.</strong></Typography>
                </Alert>

                {prefilledFromGeneral &&
                    <Typography textAlign={"center"} fontSize={"15px"}>
                        <strong>We&apos;ve pre-filled the sub-applicants already approved on your General DUC. Remove any that shouldn&apos;t be included, or add more below.</strong>
                    </Typography>
                }

                {(props.isAddSub || (props.isEdit && props.isSecond)) &&
                  <Typography textAlign={"center"} fontSize={"15px"}>
                      <strong> Previously approved sub-applicants can only be removed through the Investigator Dashboard </strong>
                  </Typography>
                }
            </Stack>


            <Stack flexDirection="column" gap={4}>
                <Stack overflow="auto" gap={4} height="500px">
                    {fields.map((field, index) => {
                        const disabled = !checkIfNew(getValues()?.subApplicants?.[index]?.email, index)
                        return (
                            <Stack key={`field-${index}`} flexDirection="column" gap={4}>
                                <Stack flexDirection="row" gap={1}>
                                    {!disabled &&
                                        <RemoveCircleOutlineIcon
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => {handleClickRemove(index)}}/>}

                                    <Typography
                                        variant="h2"
                                        fontSize="20px"
                                        fontWeight="600"
                                    >
                                        Sub-Applicant {index + 1}
                                    </Typography>
                                </Stack>

                                <SubRecipientInfoComponent
                                    id={index}
                                    key={field.id}
                                    disabled={disabled}
                                    control={control}
                                    errors={errors.subApplicants}/>
                            </Stack>)
                    })}
                </Stack>
                <Button
                    id={"duc-subrecipients-add-new-sub-applicant-button"}
                    variant="primary"
                    onClick={handleClickAdd}
                    sx={{ alignSelf: "center" }}
                >
                    Add new Sub-applicant
                </Button>
            </Stack>


            <Stack
                flexDirection="row"
                justifyContent="space-between">

                <Button
                    id={"duc-flow-back-button"}
                    variant="tertiary"
                    onClick={handleClickBack}
                    sx={{ alignSelf: "center" }}
                >
                    Back
                </Button>

                <Button
                    id={"duc-flow-continue-button"}
                    variant="primary"
                    onClick={handleSubmit(handleClick)}
                    sx={{ alignSelf: "center" }}
                >
                    Continue
                </Button>

                <SubRecipientsErrorModal error={error} open={openModal} setOpen={setOpenModal}/>

            </Stack>

        </Stack>

    )

}

export default SubRecipients;
